import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const menuItemStyle = {
  fontFamily: "'Raleway', sans-serif",
  fontSize: "0.9rem",
  fontWeight: 400,
  letterSpacing: "0.5px",
  py: 1.5,
  px: 3,
};

export const Navigation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, signOut, isAdmin } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [language, setLanguage] = useState(i18n.language || "sv");

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMobileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setMenuAnchor(null);
    setMobileMenuAnchor(null);
  };

  const toggleLanguage = () => {
    const newLang = language === "sv" ? "en" : "sv";
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
    handleClose();
  };

  const handleLogin = () => {
    navigate("/login");
    handleClose();
  };

  const handleAdmin = () => {
    navigate("/admin");
    handleClose();
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/");
    handleClose();
  };

  const scrollToSection = (sectionId: string) => {
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        if (sectionId === "hero") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          const element = document.getElementById(sectionId);
          if (element) {
            const offset = 64; // Reduced padding to match AppBar height
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.pageYOffset - offset;
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }
        }
      }, 100);
    } else {
      if (sectionId === "hero") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          const offset = 64; // Reduced padding to match AppBar height
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }
    }
    handleClose();
  };

  const navItems = [
    { label: t("sections.start.title"), id: "hero" },
    { label: t("sections.countdown.title"), id: "countdown" },
    { label: t("sections.ceremony.title"), id: "ceremony" },
    { label: t("sections.gifts.title"), id: "gifts" },
    { label: t("sections.speeches.title"), id: "speeches" },
    { label: t("sections.accommodation.title"), id: "accommodation" },
    { label: t("sections.rsvp.title"), id: "rsvp" },
  ];

  return (
    <AppBar
      position="fixed"
      color="transparent"
      sx={{
        background: "rgba(255,255,255,0.9)",
        width: "100%",
        maxWidth: "100vw",
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "center",
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          px: { xs: 1, md: 2 },
        }}
      >
        {/* Mobile Menu Button */}
        <IconButton
          sx={{
            display: { xs: "block", md: "none" },
            position: "absolute",
            left: { xs: 8, sm: 16 },
          }}
          onClick={handleMobileMenu}
          color="primary"
        >
          <MenuIcon />
        </IconButton>

        {/* Desktop Navigation */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {navItems.map((item, index) => (
            <Button
              key={index}
              color="primary"
              onClick={() => scrollToSection(item.id)}
              sx={{
                fontFamily: "'Raleway', sans-serif",
                fontSize: "0.9rem",
                textTransform: "none",
                fontWeight: 400,
                letterSpacing: "0.5px",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        {/* Settings Menu Button */}
        <IconButton
          sx={{
            position: "absolute",
            right: { xs: 8, sm: 16 },
          }}
          onClick={handleMenu}
          color="primary"
        >
          <MenuIcon />
        </IconButton>

        {/* Mobile Menu Popup */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleClose}
          PaperProps={{
            sx: { width: 250, maxHeight: "calc(100vh - 96px)" },
          }}
        >
          {navItems.map((item, index) => (
            <MenuItem
              key={index}
              onClick={() => scrollToSection(item.id)}
              sx={menuItemStyle}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>

        {/* Settings Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: { width: 200 },
          }}
        >
          <MenuItem onClick={toggleLanguage} sx={menuItemStyle}>
            {language === "sv" ? "English" : "Svenska"}
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          {currentUser ? (
            <>
              {isAdmin ? (
                <>
                  <MenuItem onClick={handleAdmin} sx={menuItemStyle}>
                    Admin Panel
                  </MenuItem>
                  <Divider sx={{ my: 1 }} />
                </>
              ) : null}
              <MenuItem onClick={handleLogout} sx={menuItemStyle}>
                {t("auth.logout")}
              </MenuItem>
            </>
          ) : (
            <MenuItem onClick={handleLogin} sx={menuItemStyle}>
              {t("auth.login")}
            </MenuItem>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
